import {
  AlertTriangleIcon,
  Maximize2Icon,
  Minimize2Icon,
  MonitorIcon,
  MousePointer2Icon,
  PlayIcon,
  RefreshCwIcon,
} from "lucide-react";
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { cn } from "~/lib/utils";

import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";

export const REMOTE_DESKTOP_FRAME_WIDTH = 1280;
export const REMOTE_DESKTOP_FRAME_HEIGHT = 800;

export type RemoteDesktopStatusState =
  | "starting"
  | "running"
  | "stopped"
  | "error"
  | "unavailable"
  | "unknown";

export interface RemoteDesktopStatus {
  readonly state: RemoteDesktopStatusState;
  readonly label?: string;
  readonly message?: string;
}

export interface RemoteDesktopControl {
  readonly available: boolean;
  readonly heldByCurrentSession: boolean;
  readonly controller?: "none" | "agent" | "human" | "other";
  /** Hosts may explicitly tell the client whether the lease can be acquired. */
  readonly canTakeControl?: boolean;
}

export type RemoteDesktopModifier = "Alt" | "Control" | "Meta" | "Shift";

export type RemoteDesktopInput =
  | {
      readonly type: "pointer";
      readonly event: "move" | "down" | "up";
      readonly x: number;
      readonly y: number;
      readonly button?: number;
    }
  | {
      readonly type: "key";
      readonly event: "press";
      readonly key: string;
      readonly modifiers?: readonly RemoteDesktopModifier[];
    }
  | { readonly type: "text"; readonly text: string }
  | {
      readonly type: "scroll";
      readonly deltaX: number;
      readonly deltaY: number;
      readonly x?: number;
      readonly y?: number;
    };

type MaybePromise = void | Promise<void>;

export interface RemoteDesktopSurfaceProps {
  /** A complete data URL, for example `data:image/jpeg;base64,...`. */
  readonly frameDataUrl: string | null;
  /** A live RFB renderer. Its canvas owns input while it is present. */
  readonly rfb?: ReactNode;
  /** Keep the RFB renderer mounted while its handshake is in progress. */
  readonly rfbConnected?: boolean;
  readonly status: RemoteDesktopStatus | null;
  readonly control: RemoteDesktopControl;
  /** Starts or resumes a stopped VM. The action is shown only for stopped VMs. */
  readonly resume?: () => MaybePromise;
  readonly takeControl: () => MaybePromise;
  readonly returnControl: () => MaybePromise;
  readonly refresh: () => MaybePromise;
  readonly input: (action: RemoteDesktopInput) => MaybePromise;
}

/**
 * Maps a point in the rendered frame to the VM's fixed 1280x800 coordinate
 * space. The image element is sized to its rendered content, so contain
 * letterboxing never becomes a clickable coordinate.
 */
export function remoteDesktopPointForFrame(input: {
  readonly clientX: number;
  readonly clientY: number;
  readonly left: number;
  readonly top: number;
  readonly renderedWidth: number;
  readonly renderedHeight: number;
}): { readonly x: number; readonly y: number } | null {
  if (input.renderedWidth <= 0 || input.renderedHeight <= 0) return null;
  const x = ((input.clientX - input.left) / input.renderedWidth) * REMOTE_DESKTOP_FRAME_WIDTH;
  const y = ((input.clientY - input.top) / input.renderedHeight) * REMOTE_DESKTOP_FRAME_HEIGHT;
  if (x < 0 || y < 0 || x > REMOTE_DESKTOP_FRAME_WIDTH || y > REMOTE_DESKTOP_FRAME_HEIGHT) {
    return null;
  }
  return {
    x: Math.round(Math.min(REMOTE_DESKTOP_FRAME_WIDTH - 1, Math.max(0, x))),
    y: Math.round(Math.min(REMOTE_DESKTOP_FRAME_HEIGHT - 1, Math.max(0, y))),
  };
}

export function remoteDesktopKeyActionForEvent(
  event: Pick<KeyboardEvent, "altKey" | "ctrlKey" | "key" | "metaKey" | "shiftKey" | "isComposing">,
): Extract<RemoteDesktopInput, { type: "key" }> | null {
  if (event.isComposing || event.key === "Dead" || event.key === "Unidentified") return null;
  const modifiers: RemoteDesktopModifier[] = [];
  if (event.altKey) modifiers.push("Alt");
  if (event.ctrlKey) modifiers.push("Control");
  if (event.metaKey) modifiers.push("Meta");
  if (event.shiftKey) modifiers.push("Shift");
  return {
    type: "key",
    event: "press",
    key: event.key,
    ...(modifiers.length > 0 ? { modifiers } : {}),
  };
}

function statusLabel(status: RemoteDesktopStatus | null): string {
  if (status?.label?.trim()) return status.label;
  switch (status?.state) {
    case "running":
      return "VM online";
    case "starting":
      return "Starting VM";
    case "stopped":
      return "VM stopped";
    case "error":
      return "VM error";
    case "unavailable":
      return "Desktop unavailable";
    default:
      return "Connecting to VM";
  }
}

function statusDotClass(status: RemoteDesktopStatus | null): string {
  switch (status?.state) {
    case "running":
      return "bg-emerald-400";
    case "starting":
      return "bg-amber-400";
    case "error":
    case "unavailable":
      return "bg-red-400";
    case "stopped":
      return "bg-muted-foreground";
    default:
      return "bg-sky-400";
  }
}

function controlLabel(control: RemoteDesktopControl): string {
  if (!control.available) return "Watch only";
  if (control.heldByCurrentSession) return "You have control";
  if (control.controller === "human" || control.controller === "other") {
    return "Controlled elsewhere";
  }
  if (control.controller === "agent") return "Agent is active";
  return "Watch only";
}

function actionErrorMessage(error: unknown): string {
  return error instanceof Error && error.message.trim().length > 0
    ? error.message
    : "Desktop action failed. Try again.";
}

function statusMessage(status: RemoteDesktopStatus | null): string {
  switch (status?.state) {
    case "error":
      return status.message ?? "The VM desktop reported an error.";
    case "unavailable":
      return status.message ?? "The VM desktop is unavailable. Check the host connection.";
    case "stopped":
      return status.message ?? "Start the VM to see its desktop.";
    case "starting":
      return status.message ?? "Starting the VM desktop…";
    case "running":
      return status.message ?? "Waiting for the first desktop frame…";
    default:
      return status?.message ?? "Connecting to the VM desktop…";
  }
}

/**
 * A presentational remote desktop surface. Frame delivery, leases, retries,
 * and VM lifecycle remain with the caller; this component never pauses the
 * stream or implicitly returns a lease when focus changes.
 */
export function RemoteDesktopSurface({
  frameDataUrl,
  rfb = null,
  rfbConnected = false,
  status,
  control,
  resume,
  takeControl,
  returnControl,
  refresh,
  input,
}: RemoteDesktopSurfaceProps) {
  const surfaceRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLImageElement>(null);
  const [pendingAction, setPendingAction] = useState<
    "resume" | "take" | "return" | "refresh" | null
  >(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const hasControl = control.heldByCurrentSession;
  const hasRfb = rfb !== null;
  const showRfb = hasRfb && rfbConnected;
  const desktopReady = status === null || status.state === "running";
  const canTakeControl =
    control.available &&
    desktopReady &&
    !hasControl &&
    (control.canTakeControl ?? (control.controller !== "human" && control.controller !== "other"));
  const canResume = status?.state === "stopped" && resume !== undefined;

  const invokeAction = useCallback(
    (kind: "resume" | "take" | "return" | "refresh", action: () => MaybePromise) => {
      setPendingAction(kind);
      setActionError(null);
      try {
        Promise.resolve(action())
          .catch((error: unknown) => setActionError(actionErrorMessage(error)))
          .finally(() => setPendingAction((current) => (current === kind ? null : current)));
      } catch (error) {
        setActionError(actionErrorMessage(error));
        setPendingAction(null);
      }
    },
    [],
  );

  const deliverInput = useCallback(
    (action: RemoteDesktopInput) => {
      try {
        Promise.resolve(input(action)).catch((error: unknown) =>
          setActionError(actionErrorMessage(error)),
        );
      } catch (error) {
        setActionError(actionErrorMessage(error));
      }
    },
    [input],
  );

  const pointAtClient = useCallback((clientX: number, clientY: number) => {
    const frame = frameRef.current;
    if (!frame) return null;
    const rect = frame.getBoundingClientRect();
    return remoteDesktopPointForFrame({
      clientX,
      clientY,
      left: rect.left,
      top: rect.top,
      renderedWidth: rect.width,
      renderedHeight: rect.height,
    });
  }, []);

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLImageElement>) => {
      if (!hasControl || event.button < 0) return;
      const point = pointAtClient(event.clientX, event.clientY);
      if (!point) return;
      event.currentTarget.setPointerCapture(event.pointerId);
      surfaceRef.current?.focus();
      deliverInput({
        type: "pointer",
        event: "down",
        ...point,
        button: event.button + 1,
      });
    },
    [deliverInput, hasControl, pointAtClient],
  );

  const handlePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLImageElement>) => {
      if (!hasControl || event.buttons === 0) return;
      const point = pointAtClient(event.clientX, event.clientY);
      if (!point) return;
      deliverInput({ type: "pointer", event: "move", ...point });
    },
    [deliverInput, hasControl, pointAtClient],
  );

  const handlePointerUp = useCallback(
    (event: ReactPointerEvent<HTMLImageElement>) => {
      if (!hasControl || event.button < 0) return;
      const point = pointAtClient(event.clientX, event.clientY);
      if (point) {
        deliverInput({
          type: "pointer",
          event: "up",
          ...point,
          button: event.button + 1,
        });
      }
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    },
    [deliverInput, hasControl, pointAtClient],
  );

  const handleWheel = useCallback(
    (event: ReactWheelEvent<HTMLImageElement>) => {
      if (!hasControl) return;
      event.preventDefault();
      const point = pointAtClient(event.clientX, event.clientY);
      deliverInput({
        type: "scroll",
        deltaX: event.deltaX,
        deltaY: event.deltaY,
        ...(point ?? {}),
      });
    },
    [deliverInput, hasControl, pointAtClient],
  );

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (!hasControl || showRfb) return;
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest("button, input, textarea, select, [contenteditable='true']")
      ) {
        return;
      }
      const action = remoteDesktopKeyActionForEvent(event.nativeEvent);
      if (!action) return;
      event.preventDefault();
      event.stopPropagation();
      deliverInput(action);
    },
    [deliverInput, hasControl, showRfb],
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>) => {
      if (!hasControl || showRfb) return;
      const text = event.clipboardData.getData("text/plain");
      if (!text) return;
      event.preventDefault();
      deliverInput({ type: "text", text });
    },
    [deliverInput, hasControl, showRfb],
  );

  const toggleFullscreen = useCallback(() => {
    const surface = surfaceRef.current;
    if (!surface) return;
    if (document.fullscreenElement === surface) {
      void document
        .exitFullscreen()
        .catch((error: unknown) => setActionError(actionErrorMessage(error)));
      return;
    }
    if (typeof surface.requestFullscreen !== "function") {
      setActionError("Fullscreen is not supported in this browser.");
      return;
    }
    void surface
      .requestFullscreen()
      .catch((error: unknown) => setActionError(actionErrorMessage(error)));
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setFullscreen(document.fullscreenElement === surfaceRef.current);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const handleTakeControl = useCallback(
    () => invokeAction("take", takeControl),
    [invokeAction, takeControl],
  );
  const handleResume = useCallback(() => {
    if (!resume) return;
    invokeAction("resume", resume);
  }, [invokeAction, resume]);
  const handleReturnControl = useCallback(
    () => invokeAction("return", returnControl),
    [invokeAction, returnControl],
  );
  const handleRefresh = useCallback(
    () => invokeAction("refresh", refresh),
    [invokeAction, refresh],
  );
  const statusText = statusLabel(status);
  const controlText = controlLabel(control);
  const placeholderRole =
    actionError || status?.state === "error" || status?.state === "unavailable"
      ? "alert"
      : "status";
  const placeholderText =
    actionError ?? (pendingAction === "resume" ? "Starting the VM…" : statusMessage(status));

  return (
    <div
      ref={surfaceRef}
      className="relative flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden bg-black/95 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      data-remote-desktop-surface
      data-fullscreen={fullscreen}
      aria-busy={pendingAction !== null}
      tabIndex={hasControl && !showRfb ? 0 : -1}
      aria-label={
        hasControl
          ? "Remote VM desktop, keyboard and pointer control active"
          : `Remote VM desktop, ${controlText.toLowerCase()}`
      }
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
    >
      {hasRfb ? (
        <div
          className={cn(
            "absolute inset-0 flex min-h-0 min-w-0 items-center justify-center",
            showRfb ? "" : "pointer-events-none opacity-0",
          )}
          data-remote-desktop-rfb-host
        >
          {rfb}
        </div>
      ) : null}
      {!showRfb && frameDataUrl ? (
        <img
          ref={frameRef}
          className={cn(
            "max-h-full max-w-full select-none object-contain",
            hasControl ? "cursor-crosshair" : "cursor-default",
          )}
          src={frameDataUrl}
          alt="Live VM desktop"
          draggable={false}
          data-remote-desktop-frame
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onWheel={handleWheel}
        />
      ) : !showRfb ? (
        <div
          className="flex max-w-xs flex-col items-center gap-2 px-6 text-center text-sm text-white/70"
          role={placeholderRole}
        >
          {status?.state === "error" || status?.state === "unavailable" ? (
            <AlertTriangleIcon className="size-5 text-amber-300" aria-hidden />
          ) : status?.state === "starting" ? (
            <Spinner className="size-5 text-white/80" />
          ) : (
            <MonitorIcon className="size-5 text-white/60" aria-hidden />
          )}
          <span>{placeholderText}</span>
        </div>
      ) : null}

      <div className="absolute inset-x-3 top-3 z-10 flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5 rounded-lg border border-white/15 bg-black/70 p-1.5 text-xs text-white shadow-sm backdrop-blur">
          <span
            className={cn("size-1.5 shrink-0 rounded-full", statusDotClass(status))}
            aria-hidden
          />
          <span className="truncate px-1" aria-live="polite">
            {actionError ?? statusText}
          </span>
          <span className="hidden text-white/50 sm:inline" aria-hidden>
            ·
          </span>
          <span className="hidden px-1 text-white/65 sm:inline" aria-live="polite">
            {controlText}
          </span>
        </div>

        <div
          className="flex shrink-0 items-center gap-1 rounded-lg border border-white/15 bg-black/70 p-1 backdrop-blur"
          data-desktop-controls
        >
          {canResume ? (
            <Button
              size="xs"
              variant="default"
              disabled={pendingAction !== null}
              aria-busy={pendingAction === "resume"}
              onClick={handleResume}
            >
              {pendingAction === "resume" ? <Spinner aria-hidden /> : <PlayIcon aria-hidden />}
              Resume VM
            </Button>
          ) : null}
          {!hasControl ? (
            <Button
              size="xs"
              variant="outline"
              disabled={pendingAction !== null || !canTakeControl}
              onClick={handleTakeControl}
            >
              {pendingAction === "take" ? (
                <Spinner aria-hidden />
              ) : (
                <MousePointer2Icon aria-hidden />
              )}
              Take control
            </Button>
          ) : null}
          {hasControl ? (
            <Button
              size="xs"
              variant="outline"
              disabled={pendingAction !== null}
              onClick={handleReturnControl}
            >
              {pendingAction === "return" ? <Spinner aria-hidden /> : null}
              Return control
            </Button>
          ) : null}
          <Button
            size="icon-xs"
            variant="ghost"
            aria-label="Refresh desktop"
            disabled={pendingAction !== null}
            onClick={handleRefresh}
          >
            <RefreshCwIcon
              className={cn(pendingAction === "refresh" && "animate-spin")}
              aria-hidden
            />
          </Button>
          <Button
            size="icon-xs"
            variant="ghost"
            aria-label={fullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            onClick={toggleFullscreen}
          >
            {fullscreen ? <Minimize2Icon aria-hidden /> : <Maximize2Icon aria-hidden />}
          </Button>
        </div>
      </div>

      {actionError && (frameDataUrl || showRfb) ? (
        <div className="absolute inset-x-3 bottom-3 z-10 flex justify-center" role="alert">
          <p className="rounded-md border border-red-300/20 bg-red-950/80 px-3 py-1.5 text-center text-xs text-red-100 shadow-sm backdrop-blur">
            {actionError}
          </p>
        </div>
      ) : null}
    </div>
  );
}
